/**
 * MasterDataStore Service - Zustand Store
 * Single Responsibility: Centralized master data storage with snapshot for rollback
 */

import { create } from 'zustand';
import { usePathNavigatorStore } from './PathNavigatorStore';

export interface MasterDataStoreState {
  data: Record<string, any>;
  snapshot: Record<string, any>;
}

export interface MasterDataStoreActions {
  /**
   * Set new master data and create snapshot
   */
  setData: (newData: Record<string, any>) => void;

  /**
   * Get current master data
   */
  getData: () => Record<string, any>;

  /**
   * Get snapshot for rollback
   */
  getSnapshot: () => Record<string, any>;

  /**
   * Update a specific field using path
   */
  updateField: (path: string, value: any) => void;

  /**
   * Apply a change to a copy of the data and commit that copy as the new data (notifies subscribers).
   * The snapshot is not touched. If `apply` throws, nothing is committed.
   */
  syncDraft: (apply: (draft: Record<string, any>) => void) => void;

  /**
   * Reset to snapshot
   */
  reset: () => void;

  /**
   * Clear all data
   */
  clear: () => void;
}

export type MasterDataStore = MasterDataStoreState & MasterDataStoreActions;

/** Deep copy, so no two stores or snapshots share objects with the live data */
const copyData = (value: Record<string, any>): Record<string, any> =>
  typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value));

export const useMasterDataStore = create<MasterDataStore>((set, get) => ({
  data: {},
  snapshot: {},

  setData: (newData: Record<string, any>): void => {
    // The store keeps its own copy: a caller changing the object it passed in must not change the
    // master data, and the snapshot taken for reset must not share objects with the live data.
    set((state) => ({
      snapshot: copyData(state.data),
      data: copyData(newData),
    }));
  },

  syncDraft: (apply: (draft: Record<string, any>) => void): void => {
    const draft = copyData(get().data);
    apply(draft);
    set({ data: draft });
  },

  getData: (): Record<string, any> => {
    return get().data;
  },

  getSnapshot: (): Record<string, any> => {
    return get().snapshot;
  },

  updateField: (path: string, value: any): void => {
    const pathNavigator = usePathNavigatorStore.getState();
    const current = get().data;
    pathNavigator.setValue(current, path, value);
    set({ data: { ...current } });
  },

  reset: (): void => {
    set((state) => ({
      data: typeof structuredClone === 'function'
        ? structuredClone(state.snapshot)
        : JSON.parse(JSON.stringify(state.snapshot)),
    }));
  },

  clear: (): void => {
    set({ data: {}, snapshot: {} });
  },
}));
